import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SortableHeader } from './SortableHeader.js';
import type { SortDir } from './SortableHeader.js';
import { useKpiDevelopPoints } from '../../../hooks/index.js';
import { formatPt } from '../../../lib/format.js';

type DevelopPointSortKey = 'completedDate' | 'issueKey' | 'summary' | 'developPoint' | 'assignee';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 開発ポイント詳細テーブル。完了日・issueKey・課題名・開発ポイント・担当者を5列で表示。全列ヘッダーソート対応
 * @graph-connects none
 */
export function DevelopPointTable({
  data,
}: {
  data: NonNullable<ReturnType<typeof useKpiDevelopPoints>['data']>['data'];
}) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState<DevelopPointSortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const sortedData = useMemo(() => {
    if (sortKey === null) return data;
    const key = sortKey;
    return [...data].sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), 'ja');
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, sortKey, sortDir]);

  const handleSort = (key: DevelopPointSortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  if (data.length === 0) {
    return (
      <div className="text-center py-12 text-[var(--color-text-muted)]">
        {t('kpi.detail.noItems')}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--color-border)]">
            <th className="text-left py-2 px-3 font-medium text-[var(--color-text-muted)]">
              <SortableHeader
                label={t('kpi.detail.completedDate')}
                sortKey="completedDate"
                currentKey={sortKey}
                currentDir={sortDir}
                onSort={handleSort}
              />
            </th>
            <th className="text-left py-2 px-3 font-medium text-[var(--color-text-muted)]">
              <SortableHeader
                label={t('kpi.detail.issueKey')}
                sortKey="issueKey"
                currentKey={sortKey}
                currentDir={sortDir}
                onSort={handleSort}
              />
            </th>
            <th className="text-left py-2 px-3 font-medium text-[var(--color-text-muted)] min-w-[200px]">
              <SortableHeader
                label={t('kpi.detail.summary')}
                sortKey="summary"
                currentKey={sortKey}
                currentDir={sortDir}
                onSort={handleSort}
              />
            </th>
            <th className="py-2 px-3 font-medium text-[var(--color-text-muted)]">
              <div className="flex justify-end">
                <SortableHeader
                  label={t('kpi.detail.developPoint')}
                  sortKey="developPoint"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                />
              </div>
            </th>
            <th className="text-left py-2 px-3 font-medium text-[var(--color-text-muted)]">
              <SortableHeader
                label={t('kpi.detail.assignee')}
                sortKey="assignee"
                currentKey={sortKey}
                currentDir={sortDir}
                onSort={handleSort}
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((dp, index) => (
            <tr
              key={`${dp.issueKey}-${String(index)}`}
              className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)]"
            >
              <td className="py-2 px-3 text-[var(--color-text-primary)] whitespace-nowrap">
                {dp.completedDate}
              </td>
              <td className="py-2 px-3">
                <a
                  href={`https://air-closet.backlog.jp/view/${dp.issueKey}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[var(--color-accent-primary)] hover:underline"
                >
                  {dp.issueKey}
                </a>
              </td>
              <td className="py-2 px-3 text-[var(--color-text-primary)]">
                <div className="line-clamp-2">{dp.summary}</div>
              </td>
              <td className="py-2 px-3 text-[var(--color-text-primary)] text-right">
                {formatPt(dp.developPoint)}pt
              </td>
              <td className="py-2 px-3 text-[var(--color-text-secondary)] whitespace-nowrap">
                {dp.assignee ?? '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
